import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, FileText, Receipt, Edit, Calendar, Building2,
  DollarSign, RotateCcw, Clock, User, RefreshCw
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const FREQ_LABELS = {
  'one-time': 'One-Time', 'monthly': 'Monthly', 'quarterly': 'Quarterly',
  'semi-annual': 'Semi-Annual', 'annual': 'Annual',
};
const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700', active: 'bg-green-100 text-green-700',
  expired: 'bg-orange-100 text-orange-700', cancelled: 'bg-red-100 text-red-700',
};

export default function AgreementDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agreement, setAgreement] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [renewEndDate, setRenewEndDate] = useState('');
  const [renewing, setRenewing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [agrRes, invRes] = await Promise.all([
        api.get(`/agreements/${id}`),
        api.get(`/agreements/${id}/invoices`),
      ]);
      setAgreement(agrRes.data);
      setInvoices(invRes.data);
    } catch {
      toast.error('Failed to load agreement');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerateInvoice = async () => {
    if (!window.confirm('Generate next invoice for this agreement?')) return;
    setGenerating(true);
    try {
      const res = await api.post(`/agreements/${id}/generate-invoice`);
      toast.success(`Invoice #${res.data.invoice.invoice_number} generated`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const openRenew = () => {
    if (agreement?.start_date && agreement?.end_date) {
      try {
        const start = parseISO(agreement.start_date);
        const end = parseISO(agreement.end_date);
        const dur = end.getTime() - start.getTime();
        setRenewEndDate(format(new Date(end.getTime() + dur), 'yyyy-MM-dd'));
      } catch { setRenewEndDate(''); }
    }
    setRenewDialogOpen(true);
  };

  const handleRenew = async () => {
    if (!renewEndDate) return;
    setRenewing(true);
    try {
      await api.post(`/agreements/${id}/renew`, { new_end_date: renewEndDate });
      toast.success(`Agreement renewed until ${renewEndDate}`);
      setRenewDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to renew');
    } finally {
      setRenewing(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  }

  if (!agreement) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Agreement not found.</p>
        <Button variant="outline" onClick={() => navigate('/agreements')} className="mt-4">Back to Agreements</Button>
      </div>
    );
  }

  const a = agreement;

  return (
    <div data-testid="agreement-detail-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/agreements')} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`text-xs ${a.type === 'SLA' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{a.type}</Badge>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate" data-testid="agreement-name">{a.name}</h1>
            <Badge className={`text-xs ${STATUS_COLORS[a.status]}`}>{a.status}</Badge>
            {a.auto_invoice && <Badge className="text-[10px] bg-purple-50 text-purple-600">Auto-Invoice</Badge>}
          </div>
          <p className="text-sm text-gray-500 mt-0.5">{a.customer_name}</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={openRenew} data-testid="page-renew-btn">
            <RotateCcw className="h-4 w-4 mr-1.5" /> Renew
          </Button>
          <Button size="sm" onClick={handleGenerateInvoice} disabled={generating || !['active', 'draft'].includes(a.status)} className="bg-green-600 hover:bg-green-700" data-testid="page-generate-invoice-btn">
            <Receipt className={`h-4 w-4 mr-1.5 ${generating ? 'animate-spin' : ''}`} /> Generate Invoice
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/agreements')} data-testid="page-edit-btn">
            <Edit className="h-4 w-4 mr-1.5" /> Edit
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Key Info */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wider">Agreement Details</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-y-4 gap-x-6">
                <div>
                  <div className="text-xs text-gray-500 flex items-center gap-1"><Building2 className="w-3 h-3" /> Customer</div>
                  <div className="text-sm font-medium mt-0.5">{a.customer_name}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Start Date</div>
                  <div className="text-sm font-medium mt-0.5">{a.start_date}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> End Date</div>
                  <div className="text-sm font-medium mt-0.5">{a.end_date || 'Ongoing'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Billing</div>
                  <div className="text-sm font-medium mt-0.5">{FREQ_LABELS[a.billing_frequency]}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Payment Terms</div>
                  <div className="text-sm font-medium mt-0.5">Net {a.payment_terms_days || 30} days</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Next Invoice</div>
                  <div className="text-sm font-medium mt-0.5">{a.next_invoice_date || '—'}</div>
                </div>
                {a.renewal_count > 0 && (
                  <div>
                    <div className="text-xs text-gray-500 flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Renewals</div>
                    <div className="text-sm font-medium mt-0.5">{a.renewal_count} time(s) — last: {a.renewed_at ? format(new Date(a.renewed_at), 'MMM d, yyyy') : '—'}</div>
                  </div>
                )}
                <div>
                  <div className="text-xs text-gray-500 flex items-center gap-1"><User className="w-3 h-3" /> Created By</div>
                  <div className="text-sm font-medium mt-0.5">{a.created_by || '—'}</div>
                </div>
              </div>
              {a.description && (
                <>
                  <Separator className="my-4" />
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Description</div>
                    <p className="text-sm text-gray-700">{a.description}</p>
                  </div>
                </>
              )}
              {a.notes && (
                <div className="mt-3">
                  <div className="text-xs text-gray-500 mb-1">Notes</div>
                  <p className="text-sm text-gray-600 italic">{a.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wider">Line Items</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="line-items-table">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs font-semibold text-gray-500 uppercase">
                      <th className="pb-2">Description</th>
                      <th className="pb-2 text-right w-20">Qty</th>
                      <th className="pb-2 text-right w-24">Unit Price</th>
                      <th className="pb-2 text-right w-24">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(a.line_items || []).map((item, i) => (
                      <tr key={i}>
                        <td className="py-2.5 text-gray-800">{item.description}</td>
                        <td className="py-2.5 text-right text-gray-600">{item.quantity}</td>
                        <td className="py-2.5 text-right text-gray-600">${item.unit_price?.toLocaleString()}</td>
                        <td className="py-2.5 text-right font-medium">${item.amount?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Separator className="my-3" />
              <div className="flex justify-end gap-6 text-sm">
                <div className="text-gray-500">Subtotal: <span className="font-medium text-gray-800">${(a.subtotal || 0).toLocaleString()}</span></div>
                {(a.tax_amount || 0) > 0 && <div className="text-gray-500">Tax ({a.tax_rate}%): <span className="font-medium text-gray-800">${a.tax_amount?.toLocaleString()}</span></div>}
                <div className="text-gray-900 font-bold text-base">Total: ${(a.total || 0).toLocaleString()}</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column: Invoices */}
        <div className="space-y-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Invoices ({invoices.length})</h2>
                <Button size="sm" variant="outline" onClick={handleGenerateInvoice} disabled={generating || !['active', 'draft'].includes(a.status)} data-testid="sidebar-generate-btn">
                  <Receipt className="h-3.5 w-3.5 mr-1" /> Generate
                </Button>
              </div>
              {invoices.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No invoices generated yet.</p>
              ) : (
                <div className="space-y-2" data-testid="invoices-list">
                  {invoices.map(inv => (
                    <button
                      key={inv.id}
                      onClick={() => navigate(`/invoices/${inv.id}`)}
                      className="w-full text-left flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                      data-testid={`invoice-${inv.id}`}
                    >
                      <div>
                        <span className="text-sm font-medium text-blue-600">#{inv.invoice_number}</span>
                        <div className="text-xs text-gray-500 mt-0.5">{inv.issue_date ? format(new Date(inv.issue_date), 'MMM d, yyyy') : ''}</div>
                      </div>
                      <div className="text-right">
                        <Badge className={`text-[10px] ${inv.status === 'paid' ? 'bg-green-100 text-green-700' : inv.status === 'sent' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{inv.status}</Badge>
                        <div className="text-sm font-semibold mt-0.5">${(inv.total || 0).toLocaleString()}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardContent className="p-6">
              <h2 className="text-sm font-semibold text-gray-700 mb-4 uppercase tracking-wider">Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Invoices Generated</span>
                  <span className="font-medium">{a.invoices_generated || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Total Invoiced</span>
                  <span className="font-medium">${invoices.reduce((s, i) => s + (i.total || 0), 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Paid</span>
                  <span className="font-medium text-green-600">${invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Outstanding</span>
                  <span className="font-medium text-orange-600">${invoices.filter(i => i.status !== 'paid').reduce((s, i) => s + (i.total || 0), 0).toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Renew Dialog */}
      <Dialog open={renewDialogOpen} onOpenChange={setRenewDialogOpen}>
        <DialogContent className="max-w-md" data-testid="page-renew-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-green-600" /> Renew Agreement
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-gray-50 rounded-lg p-4 text-sm">
              <div className="font-semibold">{a.name}</div>
              <div className="text-xs text-gray-500 mt-1">Current end: <span className="font-medium text-red-600">{a.end_date}</span></div>
            </div>
            <div>
              <Label>New End Date</Label>
              <Input type="date" value={renewEndDate} onChange={e => setRenewEndDate(e.target.value)} min={a.end_date} data-testid="page-renew-end-date" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRenewDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleRenew} disabled={renewing || !renewEndDate} className="bg-green-600 hover:bg-green-700" data-testid="page-confirm-renew-btn">
                <RotateCcw className={`h-4 w-4 mr-1.5 ${renewing ? 'animate-spin' : ''}`} />
                {renewing ? 'Renewing...' : 'Renew'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
